import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import SegmentChartCardKebabMenu from "./SegmentChartCardKebabMenu";
import { TotalSaleGroupByDropdown } from "./TotalSaleGroupedBarCard";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import {
  STATIC_SEGMENT_BAR_COLORS,
  STATIC_SEGMENT_DONUT,
  buildSegmentValueSeries,
  getSegmentGraphSlice,
} from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

/** Max slices in the segment donut legend + chart; remainder rolls into “Others”. */
const SEGMENT_DONUT_MAX_SLICES = 10;

/** Last color in {@link STATIC_SEGMENT_DONUT} — used for the aggregated “Others” slice. */
const SEGMENT_DONUT_OTHERS_COLOR = STATIC_SEGMENT_DONUT.colors[STATIC_SEGMENT_DONUT.colors.length - 1];

const AXIS_OPTIONS = /** @type {const} */ ([
  { id: "item", labelKey: "Item", defaultLabel: "Item" },
  { id: "collection", labelKey: "Collection", defaultLabel: "Collection" },
]);

const SALES_TYPE_OPTIONS = /** @type {const} */ ([
  { id: "total_sales", labelKey: "Total sales", defaultLabel: "Total sales" },
  { id: "custom", labelKey: "Custom", defaultLabel: "Custom" },
  { id: "repair", labelKey: "Repair", defaultLabel: "Repair" },
]);

const SEGMENT_SALES_TYPE_IDS = new Set(SALES_TYPE_OPTIONS.map((o) => o.id));

/**
 * @param {{ value: string; options: ReadonlyArray<{ id: string; labelKey: string; defaultLabel: string }>; onChange?: (id: string) => void; ariaLabelKey?: string; ariaDefault?: string; menuAlign?: 'left' | 'right' }} props
 */
function SegmentPlainDropdown({ value, options, onChange, ariaLabelKey, ariaDefault, menuAlign = "left" }) {
  const { t } = useTranslation();
  const menuId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const selected = options.find((o) => o.id === value) ?? options[0];
  const triggerLabel = t(selected.labelKey, { defaultValue: selected.defaultLabel });

  const pick = (id) => {
    onChange?.(id);
    close();
  };

  const menuClass =
    menuAlign === "right"
      ? "pos-dashboard-chart-card__groupby-menu pos-dashboard-chart-card__groupby-menu--segment-wide"
      : "pos-dashboard-chart-card__groupby-menu pos-dashboard-chart-card__groupby-menu--segment-axis";

  return (
    <div className="pos-dashboard-chart-card__groupby pos-dashboard-chart-card__groupby--segment" ref={wrapRef}>
      <button
        type="button"
        className="pos-dashboard-chart-card__groupby-trigger pos-dashboard-chart-card__groupby-trigger--segment-title"
        aria-label={t(ariaLabelKey ?? "Select option", { defaultValue: ariaDefault ?? "Select option" })}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pos-dashboard-chart-card__groupby-trigger-text">{triggerLabel}</span>
        <span className="pos-dashboard-chart-card__groupby-chevron" aria-hidden>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="#8A8A8A" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open ? (
        <div id={menuId} className={menuClass} role="listbox">
          <div className="pos-dashboard-chart-card__groupby-menu-inner">
            {options.map((opt) => {
              const lbl = t(opt.labelKey, { defaultValue: opt.defaultLabel });
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={value === opt.id}
                  className={`pos-dashboard-chart-card__groupby-option ${value === opt.id ? "pos-dashboard-chart-card__groupby-option--selected" : ""}`}
                  onClick={() => pick(opt.id)}
                >
                  <span className="pos-dashboard-chart-card__groupby-option-row">{lbl}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatAmountCell(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function formatQtyCell(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return String(Math.round(v));
}

function formatSegmentMetric(n, useQty) {
  return useQty ? formatQtyCell(n) : formatAmountCell(n);
}

/**
 * @param {(k: string, o?: { defaultValue: string }) => string} t
 * @param {string} salesTypeId
 */
function segmentDatasetLabel(t, salesTypeId) {
  switch (salesTypeId) {
    case "custom":
      return t("Custom", { defaultValue: "Custom" });
    case "repair":
      return t("Repair", { defaultValue: "Repair" });
    case "exchange":
      return t("Exchange", { defaultValue: "Exchange" });
    case "refund":
      return t("Refund", { defaultValue: "Refund" });
    case "total_sales":
    default:
      return t("Total sales", { defaultValue: "Total sales" });
  }
}

/**
 * Sort by value (desc), then keep top `maxSlices` slices and merge the rest into `othersLabel`.
 * The left legend and donut always follow this order so the biggest segments appear first.
 *
 * @param {{ labels: string[]; values: number[]; legendCollections: string[]; colors?: string[] }} slices
 * @param {number} maxSlices
 * @param {string} othersLabel
 */
function capSegmentDonutToTopN(slices, maxSlices, othersLabel) {
  const { labels, values, legendCollections } = slices;
  const len = labels.length;
  if (len < 1) {
    return {
      labels: [],
      values: [],
      legendCollections: [],
      colors: [],
    };
  }

  const rows = labels.map((label, i) => ({
    label,
    value: values[i],
    collection: legendCollections[i] ?? "",
  }));
  rows.sort((a, b) => b.value - a.value);

  const colorAt = (i) => STATIC_SEGMENT_BAR_COLORS[i % STATIC_SEGMENT_BAR_COLORS.length];

  if (len <= maxSlices) {
    return {
      labels: rows.map((r) => r.label),
      values: rows.map((r) => r.value),
      legendCollections: rows.map((r) => r.collection),
      colors: rows.map((_, i) => colorAt(i)),
    };
  }

  const top = rows.slice(0, maxSlices);
  const rest = rows.slice(maxSlices);
  const othersSum = rest.reduce((s, r) => s + r.value, 0);

  const outLabels = top.map((r) => r.label);
  const outValues = top.map((r) => r.value);
  const outLegendCollections = top.map((r) => r.collection);
  const outColors = top.map((_, i) => colorAt(i));

  if (othersSum > 0) {
    outLabels.push(othersLabel);
    outValues.push(othersSum);
    outLegendCollections.push("");
    outColors.push(SEGMENT_DONUT_OTHERS_COLOR);
  }

  return {
    labels: outLabels,
    values: outValues,
    legendCollections: outLegendCollections,
    colors: outColors,
  };
}

/**
 * Table rows: top `maxSlices` by amount, remainder as `othersLabel` (same total / % basis as donut).
 *
 * @param {{ key: string; label: string; collection: string; amount: number }[]} rows
 * @param {number} maxSlices
 * @param {string} othersLabel
 * @param {number} totalForPct
 */
function capSegmentTableRowsToTopN(rows, maxSlices, othersLabel, totalForPct) {
  const withPct = (list) =>
    list.map((r) => ({
      ...r,
      pct: totalForPct ? Math.round((r.amount / totalForPct) * 100) : 0,
    }));

  if (rows.length <= maxSlices) {
    return withPct(rows);
  }

  const sorted = [...rows].sort((a, b) => b.amount - a.amount);
  const top = sorted.slice(0, maxSlices);
  const rest = sorted.slice(maxSlices);
  const othersAmount = rest.reduce((s, r) => s + r.amount, 0);
  const out = withPct(top);
  if (othersAmount > 0) {
    out.push({
      key: "segment-table-others",
      label: othersLabel,
      collection: "",
      amount: othersAmount,
      pct: totalForPct ? Math.round((othersAmount / totalForPct) * 100) : 0,
    });
  }
  return out;
}

/**
 * @param {import('./dashboardChartsModel.js').TotalSaleGroupedModel | undefined} model
 * @param {string} axisId
 * @param {string} salesTypeId
 * @param {boolean} useQty
 */
function buildSegmentDonutSlices(model, axisId, salesTypeId, useQty) {
  const categories = model?.categories ?? [];
  const collections = model?.collections ?? [];
  const valuesRow = buildSegmentValueSeries(model, salesTypeId, useQty);
  const n = Math.min(categories.length, valuesRow.length);

  /** @type {{ labels: string[]; values: number[]; legendCollections: string[] }} */
  const empty = { labels: [], values: [], legendCollections: [] };
  if (n < 1) {
    return { ...empty, colors: [], total: 0 };
  }

  if (axisId === "collection") {
    /** @type {Map<string, number>} */
    const sums = new Map();
    for (let i = 0; i < n; i += 1) {
      const item = categories[i] ?? "";
      const col = collections[i] ?? "";
      const key = col || item;
      const amt = Number(valuesRow[i]);
      const v = Number.isFinite(amt) ? amt : 0;
      sums.set(key, (sums.get(key) ?? 0) + v);
    }
    const entries = Array.from(sums.entries()).sort((a, b) => b[1] - a[1]);
    const labels = entries.map((e) => e[0]);
    const values = entries.map((e) => e[1]);
    const legendCollections = labels.map(() => "");
    const colors = labels.map((_, i) => STATIC_SEGMENT_BAR_COLORS[i % STATIC_SEGMENT_BAR_COLORS.length]);
    const total = values.reduce((a, b) => a + b, 0);
    return { labels, values, colors, legendCollections, total };
  }

  const labels = [];
  const values = [];
  const legendCollections = [];
  for (let i = 0; i < n; i += 1) {
    labels.push(categories[i] ?? "");
    const amt = Number(valuesRow[i]);
    values.push(Number.isFinite(amt) ? amt : 0);
    legendCollections.push(collections[i] ?? "");
  }
  const colors = labels.map((_, i) => STATIC_SEGMENT_BAR_COLORS[i % STATIC_SEGMENT_BAR_COLORS.length]);
  const total = values.reduce((a, b) => a + b, 0);
  return { labels, values, colors, legendCollections, total };
}

/**
 * Donut slices: `segment-graph` API when `segmentGraphModel` is set, else {@link buildSegmentDonutSlices} from total-sale model.
 *
 * @param {import('./dashboardChartsModel.js').TotalSaleGroupedModel | undefined} model
 * @param {import('./dashboardChartsModel.js').SegmentGraphModel | null | undefined} segmentGraphModel
 */
function resolveSegmentDonutDerived(model, segmentGraphModel, axisId, salesTypeId, useQty) {
  if (segmentGraphModel != null) {
    const slice = getSegmentGraphSlice(segmentGraphModel, axisId, salesTypeId);
    if (!slice || slice.labels.length < 1) {
      return { labels: [], values: [], legendCollections: [], colors: [], total: 0 };
    }
    const labels = slice.labels.map((l) => String(l));
    const values = labels.map((_, i) => {
      const raw = useQty ? slice.qty[i] : slice.amount[i];
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    });
    const legendCollections = labels.map(() => "");
    const colors = labels.map((_, i) => STATIC_SEGMENT_BAR_COLORS[i % STATIC_SEGMENT_BAR_COLORS.length]);
    const total = values.reduce((a, b) => a + b, 0);
    return { labels, values, legendCollections, colors, total };
  }
  return buildSegmentDonutSlices(model, axisId, salesTypeId, useQty);
}

/**
 * @param {{ model: import('./dashboardChartsModel.js').TotalSaleGroupedModel | undefined; segmentGraphModel?: import('./dashboardChartsModel.js').SegmentGraphModel | null; axisId: string; salesTypeId: string; useQty: boolean }} props
 */
function SegmentDonutBody({ model, segmentGraphModel, axisId, salesTypeId, useQty }) {
  const { t } = useTranslation();

  const derived = useMemo(
    () => resolveSegmentDonutDerived(model, segmentGraphModel, axisId, salesTypeId, useQty),
    [model, segmentGraphModel, axisId, salesTypeId, useQty],
  );

  const { labels, values, colors, donutColl, usingStatic } = useMemo(() => {
    if (derived.labels.length < 1) {
      if (segmentGraphModel != null) {
        return {
          labels: [],
          values: [],
          colors: [],
          donutColl: [],
          usingStatic: false,
        };
      }
      return {
        labels: [],
        values: [],
        colors: [],
        donutColl: [],
        usingStatic: false,
      };
    }
    const othersLabel = t("Others", { defaultValue: "Others" });
    const capped = capSegmentDonutToTopN(
      {
        labels: derived.labels,
        values: derived.values,
        legendCollections: derived.legendCollections,
        colors: derived.colors,
      },
      SEGMENT_DONUT_MAX_SLICES,
      othersLabel,
    );
    return {
      labels: capped.labels,
      values: capped.values,
      colors: capped.colors,
      donutColl: capped.legendCollections,
      usingStatic: false,
    };
  }, [derived, segmentGraphModel, t]);
  const total = useMemo(() => values.reduce((a, b) => a + b, 0), [values]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderWidth: 0,
        },
      ],
    }),
    [labels, values, colors],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      interaction: { mode: "nearest", intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const ctx0 = items[0];
              if (!ctx0) return [];
              const i = ctx0.dataIndex;
              const base = String(ctx0.label ?? "");
              const c = donutColl[i];
              const tail = axisId === "item" ? "" : c ? ` · ${c}` : "";
              return [`${base}${tail}:`];
            },
            label: (ctx) => {
              const v = ctx.parsed;
              const pct = total ? Math.round((v / total) * 100) : 0;
              return `${formatSegmentMetric(v, useQty)} (${pct}%)`;
            },
          },
        },
      },
    }),
    [total, donutColl, axisId, useQty],
  );

  const rows = useMemo(() => {
    return labels.map((label, i) => ({
      key: `${label}-${i}`,
      label,
      collection: donutColl[i] ?? "",
      value: values[i],
      pct: total ? Math.round((values[i] / total) * 100) : 0,
      color: colors[i],
    }));
  }, [labels, values, colors, total, donutColl]);

  const legendRows = usingStatic ? rows : rows.slice(0, SEGMENT_DONUT_MAX_SLICES + 1);

  return (
    <div className="pos-segment-donut" aria-label={t("Segment breakdown", { defaultValue: "Segment breakdown" })}>
      <div className="pos-segment-donut__legend" role="list">
        {legendRows.map((row) => (
          <div key={row.key} className="pos-segment-donut__legend-row" role="listitem">
            <span className="pos-segment-donut__legend-name">
              <span className="pos-segment-donut__swatch" style={{ background: row.color }} aria-hidden />
              <span className="pos-segment-donut__legend-text">
                <span className="pos-segment-donut__legend-item">{row.label}</span>
                {axisId !== "item" && row.collection ? (
                  <span className="pos-segment-donut__legend-collection">{row.collection}</span>
                ) : null}
              </span>
            </span>
            <span className="pos-segment-donut__legend-value">{formatSegmentMetric(row.value, useQty)}</span>
            <span className="pos-segment-donut__legend-pct">({row.pct}%)</span>
          </div>
        ))}
      </div>
      <div className="pos-segment-donut__chart">
        <div className="pos-segment-donut__canvas">
          {labels.length > 0 ? (
            <Doughnut data={chartData} options={options} />
          ) : (
            <div className="pos-segment-donut__empty" role="status">
              {usingStatic ? null : t("No data", { defaultValue: "No data" })}
            </div>
          )}
        </div>
        <div className="pos-segment-donut__center">
          <span className="pos-segment-donut__center-label">
            {useQty ? t("Total Qty", { defaultValue: "Total Qty" }) : t("Total Sale", { defaultValue: "Total Sale" })}
          </span>
          <span className="pos-segment-donut__center-value">{formatSegmentMetric(total, useQty)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ model: import('./dashboardChartsModel.js').TotalSaleGroupedModel; segmentGraphModel?: import('./dashboardChartsModel.js').SegmentGraphModel | null; axisId: string; salesTypeId: string; useQty: boolean }} props
 */
function SegmentBarBody({ model, segmentGraphModel, axisId, salesTypeId, useQty }) {
  const { t } = useTranslation();

  const { chartData, options } = useMemo(() => {
    /** @type {string[]} */
    let categories;
    /** @type {string[]} */
    let collections;
    /** @type {number[]} */
    let series;
    /** @type {string[]} */
    let xL;

    if (segmentGraphModel != null) {
      const slice = getSegmentGraphSlice(segmentGraphModel, axisId, salesTypeId);
      xL = slice && slice.labels.length > 0 ? slice.labels.map((lab) => String(lab)) : [];
      series = xL.map((_, i) => {
        if (!slice) return 0;
        const raw = useQty ? slice.qty[i] : slice.amount[i];
        const nv = Number(raw);
        return Number.isFinite(nv) ? nv : 0;
      });
      categories = axisId === "item" ? xL : xL.map(() => "\u2014");
      collections = axisId === "collection" ? xL : xL.map(() => "");
    } else {
      categories = model?.categories ?? [];
      collections = model?.collections ?? [];
      series = buildSegmentValueSeries(model, salesTypeId, useQty);
      const m = Math.min(categories.length, series.length);
      xL = [];
      for (let i = 0; i < m; i += 1) {
        const item = categories[i] ?? "";
        const col = collections[i] ?? "";
        if (axisId === "collection") {
          xL.push(col || item);
        } else {
          xL.push(item);
        }
      }
    }

    const n = xL.length;
    const bg = xL.map((_, i) => STATIC_SEGMENT_BAR_COLORS[i % STATIC_SEGMENT_BAR_COLORS.length]);
    const dsLabel = `${segmentDatasetLabel(t, salesTypeId)} (${useQty ? t("Qty", { defaultValue: "Qty" }) : t("Amount", { defaultValue: "Amount" })})`;
    const data = {
      labels: xL,
      datasets: [
        {
          label: dsLabel,
          data: series.slice(0, n),
          backgroundColor: bg,
          maxBarThickness: 28,
          borderRadius: 0,
          borderSkipped: false,
        },
      ],
    };

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          align: "center",
          onClick: () => {},
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: "circle",
            padding: 16,
            color: "#2A2A2A",
            font: { family: "'Segoe UI', Roboto, sans-serif", size: 12 },
            generateLabels: (chart) => {
              const ds = chart.data.datasets[0];
              const labels = chart.data.labels ?? [];
              const colors = Array.isArray(ds.backgroundColor) ? ds.backgroundColor : [];
              return labels.map((text, i) => ({
                text: String(text),
                fillStyle: colors[i],
                strokeStyle: colors[i],
                fontColor: "#2A2A2A",
                hidden: false,
                index: i,
                datasetIndex: 0,
              }));
            },
          },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            title: (items) => {
              const i = items[0]?.dataIndex ?? 0;
              const item = categories[i] ?? "";
              const col = collections[i] ?? "";
              let text = "";
              if (axisId === "item") text = item || col || "";
              else if (segmentGraphModel != null) text = col || item;
              else if (item && col) text = `${item} · ${col}`;
              else text = item || col || "";
              return text ? [`${text}:`] : [];
            },
            label: (ctx) => formatSegmentMetric(Number(ctx.parsed?.y), useQty),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { family: "'Segoe UI', Roboto, sans-serif", size: 12 },
            color: "#757575",
          },
        },
        y: {
          min: 0,
          border: { display: false },
          grid: { color: "#E6E6E6" },
          title: {
            display: true,
            text: useQty ? t("Quantity", { defaultValue: "Quantity" }) : t("Amount", { defaultValue: "Amount" }),
            color: "#373D3F",
            font: { size: 11 },
          },
          ticks: {
            font: { family: "'Segoe UI', Roboto, sans-serif", size: 12 },
            color: "#373D3F",
            callback: (v) => {
              const nv = Number(v);
              if (!Number.isFinite(nv)) return "";
              if (useQty) return String(Math.round(nv));
              if (nv >= 1000) return `${Math.round(nv / 1000)}K`;
              return String(nv);
            },
          },
        },
      },
    };
    return { chartData: data, options: opts };
  }, [model, segmentGraphModel, axisId, salesTypeId, useQty, t]);

  return (
    <div className="pos-segment-bar">
      {chartData.labels.length > 0 ? <Bar data={chartData} options={options} /> : null}
    </div>
  );
}

/**
 * Segment table — Item, Collection, metric, % — rows follow {@link TotalSaleGroupedModel} or segment API slice.
 *
 * @param {{ model: import('./dashboardChartsModel.js').TotalSaleGroupedModel; segmentGraphModel?: import('./dashboardChartsModel.js').SegmentGraphModel | null; axisId: string; salesTypeId: string; useQty: boolean }} props
 */
function SegmentTableBody({ model, segmentGraphModel, axisId, salesTypeId, useQty }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {'label'|'collection'|'amount'|'pct'} */ ("amount"),
    dir: /** @type {'asc'|'desc'} */ ("desc"),
  }));

  useEffect(() => {
    setSort({ key: "amount", dir: "desc" });
  }, [model, segmentGraphModel, axisId, salesTypeId, useQty]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const cappedRows = useMemo(() => {
    if (segmentGraphModel != null) {
      const slice = getSegmentGraphSlice(segmentGraphModel, axisId, salesTypeId);
      /** @type {{ key: string; label: string; collection: string; amount: number }[]} */
      let pairs = [];
      if (!slice || slice.labels.length < 1) {
        return [];
      }
      if (axisId === "collection") {
        slice.labels.forEach((lab, i) => {
          const raw = useQty ? slice.qty[i] : slice.amount[i];
          const amount = Number(raw);
          pairs.push({
            key: `segment-api-col-${String(lab)}-${i}`,
            label: "\u2014",
            collection: String(lab),
            amount: Number.isFinite(amount) ? amount : 0,
          });
        });
      } else {
        slice.labels.forEach((lab, i) => {
          const raw = useQty ? slice.qty[i] : slice.amount[i];
          const amount = Number(raw);
          pairs.push({
            key: `segment-api-item-${String(lab)}-${i}`,
            label: String(lab),
            collection: "",
            amount: Number.isFinite(amount) ? amount : 0,
          });
        });
      }
      const total = pairs.reduce((a, r) => a + r.amount, 0);
      pairs.sort((a, b) => b.amount - a.amount);
      const othersLabel = t("Others", { defaultValue: "Others" });
      return capSegmentTableRowsToTopN(pairs, SEGMENT_DONUT_MAX_SLICES, othersLabel, total);
    }

    const categories = model?.categories ?? [];
    const collections = model?.collections ?? [];
    const series = buildSegmentValueSeries(model, salesTypeId, useQty);
    const n = Math.min(categories.length, series.length);

    /** @type {{ key: string; label: string; collection: string; amount: number }[]} */
    let pairs = [];

    if (axisId === "collection") {
      /** @type {Map<string, number>} */
      const sums = new Map();
      for (let i = 0; i < n; i += 1) {
        const item = categories[i] ?? "";
        const col = collections[i] ?? "";
        const key = col || item;
        const amount = Number(series[i]);
        const v = Number.isFinite(amount) ? amount : 0;
        sums.set(key, (sums.get(key) ?? 0) + v);
      }
      let idx = 0;
      for (const [name, amount] of sums.entries()) {
        pairs.push({
          key: `segment-col-${name}-${idx}`,
          label: "\u2014",
          collection: name,
          amount,
        });
        idx += 1;
      }
    } else {
      for (let i = 0; i < n; i += 1) {
        const amount = Number(series[i]);
        pairs.push({
          key: `${categories[i]}-${i}`,
          label: categories[i] ?? "",
          collection: collections[i] ?? "",
          amount: Number.isFinite(amount) ? amount : 0,
        });
      }
    }

    const total = pairs.reduce((a, r) => a + r.amount, 0);
    pairs.sort((a, b) => b.amount - a.amount);
    const othersLabel = t("Others", { defaultValue: "Others" });
    return capSegmentTableRowsToTopN(pairs, SEGMENT_DONUT_MAX_SLICES, othersLabel, total);
  }, [model, segmentGraphModel, axisId, salesTypeId, useQty, t]);

  const rows = useMemo(() => {
    const copy = cappedRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case "label":
          c = String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" });
          break;
        case "collection":
          c = String(a.collection).localeCompare(String(b.collection), undefined, { sensitivity: "base" });
          break;
        case "amount":
          c = a.amount - b.amount;
          break;
        case "pct":
          c = a.pct - b.pct;
          break;
        default:
          c = 0;
      }
      return c * mul;
    });
    return copy;
  }, [cappedRows, sort]);

  const byItem = axisId === "item";

  const thBase = "pos-segment-table__th";
  const thSorted = "pos-segment-table__th--sorted";
  const valueColumnLabel = useQty ? t("Qty", { defaultValue: "Qty" }) : t("Sale", { defaultValue: "Sale" });

  return (
    <div
      className={`pos-segment-table${byItem ? " pos-segment-table--by-item" : ""}`}
      role="table"
      aria-label={t("Segment table", { defaultValue: "Segment table" })}
    >
      <div className="pos-segment-table__head" role="rowgroup">
        <div className="pos-segment-table__head-row" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-segment-table__th--item"
            ariaSortLabel={t("Sort by item", { defaultValue: "Sort by item" })}
            sorted={sort.key === "label"}
            direction={sort.dir}
            onSort={() => toggleSort("label")}
          >
            {t("Item", { defaultValue: "Item" })}
          </DashboardChartTableSortHeader>
          {byItem ? null : (
            <DashboardChartTableSortHeader
              baseClassName={thBase}
              sortedClassName={thSorted}
              className="pos-segment-table__th--collection"
              ariaSortLabel={t("Sort by collection", { defaultValue: "Sort by collection" })}
              sorted={sort.key === "collection"}
              direction={sort.dir}
              onSort={() => toggleSort("collection")}
            >
              {t("Collection", { defaultValue: "Collection" })}
            </DashboardChartTableSortHeader>
          )}
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-segment-table__th--sale"
            ariaSortLabel={
              useQty ? t("Sort by quantity", { defaultValue: "Sort by quantity" }) : t("Sort by sale", { defaultValue: "Sort by sale" })
            }
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => toggleSort("amount")}
          >
            {valueColumnLabel}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-segment-table__th--pct"
            ariaSortLabel={t("Sort by percent", { defaultValue: "Sort by percent" })}
            sorted={sort.key === "pct"}
            direction={sort.dir}
            onSort={() => toggleSort("pct")}
          >
            {t("%", { defaultValue: "%" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-segment-table__scroll" role="rowgroup">
        {rows.map((row) => (
          <div key={row.key} className="pos-segment-table__row" role="row">
            <div className="pos-segment-table__td pos-segment-table__td--item" role="cell">
              {row.label}
            </div>
            {byItem ? null : (
              <div className="pos-segment-table__td pos-segment-table__td--collection" role="cell">
                {row.collection}
              </div>
            )}
            <div className="pos-segment-table__td pos-segment-table__td--sale" role="cell">
              {formatSegmentMetric(row.amount, useQty)}
            </div>
            <div className="pos-segment-table__td pos-segment-table__td--pct" role="cell">
              {row.pct}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Item / Collection “Segment” card — donut, bar, table (kebab), header filters per Figma.
 *
 * @param {object} props
 * @param {import('./dashboardChartsModel.js').TotalSaleGroupedModel} props.model — fallback when segment API not loaded.
 * @param {import('./dashboardChartsModel.js').SegmentGraphModel | null} [props.segmentGraphModel] — from `segment-graph` API.
 * @param {() => void} [props.onMenuRemove]
 */
function SegmentChartCard({ model, segmentGraphModel, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewKind, setViewKind] = useState(/** @type {'donut'|'bar'|'table'} */ ("donut"));
  const [axisId, setAxisId] = useState("item");
  const [salesTypeId, setSalesTypeId] = useState("total_sales");
  /** Local only — does not sync with {@link TotalSaleGroupedBarCard} groupBy. */
  const [groupBy, setGroupBy] = useState(/** @type {'amount'|'quantity'} */ ("amount"));

  useEffect(() => {
    if (!SEGMENT_SALES_TYPE_IDS.has(salesTypeId)) setSalesTypeId("total_sales");
  }, [salesTypeId]);

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("segment"));
  }, []);

  const handleGroupBy = useCallback((v) => {
    const next = v === "quantity" ? "quantity" : "amount";
    setGroupBy(next);
  }, []);

  const useQty = groupBy === "quantity";

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--segment${viewKind === "table" ? " pos-dashboard-chart-card--segment-table" : ""}`}
    >
      <header className="pos-dashboard-chart-card__header pos-dashboard-chart-card__header--with-controls pos-dashboard-chart-card__header--segment">
        <div className="pos-dashboard-chart-card__title-cluster pos-dashboard-chart-card__title-cluster--segment">
          <SegmentPlainDropdown
            value={axisId}
            onChange={setAxisId}
            options={AXIS_OPTIONS}
            ariaLabelKey="Segment by"
            ariaDefault="Segment by"
            menuAlign="left"
          />
          <h2 className="pos-dashboard-chart-card__title pos-dashboard-chart-card__title--inline">{t("Segment", { defaultValue: "Segment" })}</h2>
        </div>
        <div className="pos-dashboard-chart-card__header-actions pos-dashboard-chart-card__header-actions--segment">
          <SegmentPlainDropdown
            value={salesTypeId}
            onChange={setSalesTypeId}
            options={SALES_TYPE_OPTIONS}
            ariaLabelKey="Sales type"
            ariaDefault="Sales type"
            menuAlign="right"
          />
          <TotalSaleGroupByDropdown value={groupBy === "quantity" ? "quantity" : "amount"} onChange={handleGroupBy} />
          <SegmentChartCardKebabMenu
            viewKind={viewKind}
            onViewKindChange={setViewKind}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--segment${viewKind === "table" ? " pos-dashboard-chart-card__body--table" : ""}`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--segment">
          {viewKind === "donut" ? (
            <SegmentDonutBody
              model={model}
              segmentGraphModel={segmentGraphModel}
              axisId={axisId}
              salesTypeId={salesTypeId}
              useQty={useQty}
            />
          ) : null}
          {viewKind === "bar" ? (
            <SegmentBarBody
              model={model}
              segmentGraphModel={segmentGraphModel}
              axisId={axisId}
              salesTypeId={salesTypeId}
              useQty={useQty}
            />
          ) : null}
          {viewKind === "table" ? (
            <SegmentTableBody
              model={model}
              segmentGraphModel={segmentGraphModel}
              axisId={axisId}
              salesTypeId={salesTypeId}
              useQty={useQty}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SegmentChartCard;
